import React, { useState, useEffect } from 'react';
import ApplicationDetails from './ApplicationDetails';
import RegistrationSection from './RegistrationSection';
import BenefitsSection from './BenefitsSection';
import ApplicationHistory from './ApplicationHistory';
import RejectionAudit from './RejectionAudit';
import { useApplication } from './hooks/useApplication';
import InvitationsSection from './InvitationsSection';
import DeclineInvitationModal from './DeclineInvitationModal';
import QueuePositionSection from './QueuePositionSection';
import './ApplicationModal.css';

const ApplicationModal = ({ 
  isOpen, 
  onClose, 
  applicationId,
  mode = 'view',
  userRole
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineCallback, setDeclineCallback] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const { application, loading, error, refetch, statuses, fetchApplicationStatuses,
    declineInvitation, updateBenefits, saveBenefitsWithFiles,
    verifyBenefit, declineBenefit, checkMR, setMR, downloadFile } = useApplication(applicationId);

  useEffect(() => {
    if (isOpen && applicationId) {
      
      refetch();
      setActiveTab('details');
    }
  }, [isOpen, applicationId, refetch]);

  useEffect(() => {
    if (applicationId) {
      fetchApplicationStatuses();
    }
  }, [applicationId, fetchApplicationStatuses]);

  // Колбэк для открытия модального окна отклонения
  const handleOpenDeclineModal = (onDeclineCallback, invitation = null) => {
    setDeclineCallback(() => onDeclineCallback);
    setSelectedInvitation(invitation);
    setIsDeclineModalOpen(true);
  };

  const handleDeclineInvitation = async (reason) => {
    if (declineCallback) {
      await declineCallback(reason);
      setIsDeclineModalOpen(false);
      setDeclineCallback(null);
      setSelectedInvitation(null);
    }
  };

  const handleCloseDeclineModal = () => {
    setIsDeclineModalOpen(false);
    setDeclineCallback(null);
    setSelectedInvitation(null);
  };

  // Оновлення після змін у вкладці пільг — перечитуємо всю заявку
  const handleBenefitsChange = async () => {
    await refetch();
    await fetchApplicationStatuses();
  };

  // Функция для проверки, можно ли отклонить приглашение
  const canDeclineInvitation = () => {
    return activeTab === 'invitations' && 
           application?.currentStatus && 
           ['invite-sent', 'invite-accept'].includes(application.currentStatus);
  };

  // Обработчик клика по кнопке отклонения
  const handleDeclineButtonClick = () => {
    const availableInvitation = statuses[statuses.length - 1];

    if (availableInvitation) {
      const declineCallback = async (reason) => {
        console.log('Decline invitation with reason:', reason, availableInvitation);
        const result = await declineInvitation(reason, availableInvitation.kindergartenId, availableInvitation.groupNumber);
        fetchApplicationStatuses(applicationId);
        return alert('Заявка на вступ відхилена');
      };

      handleOpenDeclineModal(declineCallback, availableInvitation);
    }
  };

  const handleClose = () => {
    setActiveTab('details');
    onClose();
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'details', label: 'Заявка' },
    { id: 'registration', label: 'Реєстрація' },
    { id: 'benefits', label: 'Пільги' },
    { id: 'invitations', label: 'Надіслані запрошення' },
    { id: 'rejection', label: 'Аудит відхилення заявки' },
    { id: 'queue', label: 'Позиція в черзі' },
    { id: 'history', label: 'Історія' },
  ];

  if (loading) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="application-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Завантаження...</span>
              </div>
              <p className="mt-2">Завантаження даних заявки...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="application-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Помилка</h2>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
          <div className="modal-content">
            <div className="alert alert-danger m-3">
              <strong>Помилка завантаження заявки:</strong> {error}
              <div className="mt-2">
                <button className="btn btn-sm btn-outline-danger" onClick={refetch}>
                  Спробувати знову
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="application-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="alert alert-warning m-3">
              Заявка не знайдена
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="application-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-title">Заявка #{application.registrationNumber}</h2>
            <span className={`status-badge ${application.statusClass}`}>
              {application.status}
            </span>
            {false && application.queueNumber && (
              <span className="queue-number">Черга: {application.queueNumber}</span>
            )}
          </div>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="modal-content">
          {activeTab === 'details' && (
            <ApplicationDetails 
              applicationData={application} 
              mode={mode}
            />
          )}
          
          {activeTab === 'registration' && (
            <RegistrationSection 
              applicationData={application} 
              mode={mode}
              applicationId={applicationId}
              checkMR={checkMR}
              setMR={setMR}
              downloadFile={downloadFile}
              onChange={async () => { await refetch(); }}
              userRole={userRole}
            />
          )}
          
          {activeTab === 'benefits' && (
            <BenefitsSection 
              applicationData={application} 
              mode={mode}
              applicationId={applicationId}
              onChange={handleBenefitsChange}
              updateBenefits={updateBenefits}
              saveBenefitsWithFiles={saveBenefitsWithFiles}
              verifyBenefit={verifyBenefit}
              declineBenefit={declineBenefit}
              downloadFile={downloadFile}
            />
          )}
          
          {activeTab === 'invitations' && (
            <InvitationsSection 
              applicationData={application} 
              mode={mode}
              applicationId={applicationId}
              onOpenDeclineModal={handleOpenDeclineModal}
            />
          )}
          
          {activeTab === 'queue' && (
            <QueuePositionSection
              applicationData={application}
            />
          )}

          {activeTab === 'history' && (
            <ApplicationHistory 
              applicationData={application} 
              mode={mode}
              applicationId={applicationId}
            />
          )}

          {activeTab === 'rejection' && (
            <RejectionAudit 
              applicationData={application} 
              mode={mode}
              applicationId={applicationId}
            />
          )}
        </div>

        <div className="modal-actions">
          <div className="action-buttons">
            {mode === 'edit' && application.currentStatus === 'declined' && (
              <button 
                className="btn btn-success"
                onClick={() => {
                  console.log('Restore application');
                }}
              >
                Відновити заявку
              </button>
            )}
            
            {canDeclineInvitation() && (
              <button 
                className="btn btn-danger"
                onClick={handleDeclineButtonClick}
              >
                Відхилити
              </button>
            )}
            {canDeclineInvitation() && (
              <button 
                className="btn btn-success"
                onClick={() => alert('Заявка не потребує підтвердження')}
              >
                Підтвердити
              </button>
            )}
          </div>
          <button className="btn btn-secondary" onClick={handleClose}>
            Закрити
          </button>
        </div>

        <DeclineInvitationModal
          isOpen={isDeclineModalOpen}
          onClose={handleCloseDeclineModal}
          onDecline={handleDeclineInvitation}
          loading={false}
          invitation={selectedInvitation}
          applicationData={application}
        />
      </div>
    </div>
  );
};

export default ApplicationModal;